import { HttpErrorResponse } from "@angular/common/http";
import { Component, ElementRef, ViewChild, inject } from "@angular/core";
import { BusinessNavigatorOverviewModel } from "@app/navigators/business-navigator/models/business-navigator-overview-model";
import { BusinessNavigatorDataService } from "@app/navigators/business-navigator/services/business-navigator-data.service";
import { Pages } from "@constants/pages/Pages";
import { ComparisonType } from "@enums/workspace/ComparisonType";
import { nameof } from "@methods/jeffs-toolkit";
import { SearchKeyword } from "@models/workspace/SearchKeyword";
import { WidgetFilterKey } from "./../../../shared/models/WidgetFilterKey";
import { BaseHighchartWidget } from "./../BaseHighchartWidget";
import { firstValueFrom } from "rxjs";
import { provideTranslationScope } from "@app/shared/i18n/translation-scope";
import { scope } from "@app/dashboard/i18n/scope";

@Component({
    selector: "business-assessment-widget",
    templateUrl: "../highchart-widget.component.html",
    styleUrls: ["../highchart-widget.component.scss"],
    viewProviders: [provideTranslationScope(scope)],
})
export class BusinessAssessmentWidgetComponent extends BaseHighchartWidget {
    @ViewChild("chartContainer") chartContainer: ElementRef;

    private businessDataService = inject(BusinessNavigatorDataService);
    private pages = inject(Pages);

    constructor() {
        super();

        this.globalFilterWorkspaceColumns[WidgetFilterKey.GlobalFilter.Organization] =
            "organizationID";
        this.globalFilterWorkspaceColumns[WidgetFilterKey.GlobalFilter.BusinessDimension] =
            "businessID";
    }

    protected async getChartConfig() {
        this.config.noData = false;
        this.config.noDataExplanation = "";

        await this.translation.ready();

        const filters = this.getFilters();
        const data = await firstValueFrom(this.businessDataService
            .getWidgetInfo(filters))
            .catch((x: HttpErrorResponse) => {
                this.config.noData = true;
                if (
                    x.statusText == "Not Found" &&
                    filters[this.filterKeys.GlobalFilter.WorkspacePreset]
                ) {
                    this.config.noDataExplanation = this.translation.t(
                        "businessAssessmentWidget.presetNotAccessible"
                    );
                }
            });

        this.config.noData = !data || data.length === 0;
        if (this.config.noData) {
            return null;
        }

        const mappedData = [
            <any>{
                name: this.translation.t("businessAssessmentWidget.seriesName"),
                data,
            },
        ];

        const config = this.getBaseChartConfig(null, mappedData);

        config.tooltip = {
            headerFormat: "<b>{point.key}:</b> {point.y}",
            pointFormat: "",
        };

        config.chart.spacingLeft = 0;
        config.chart.spacingRight = 0;

        config.plotOptions.pie.size = "65%";
        config.plotOptions.pie.innerSize = "50%";

        return config;
    }

    protected async openWorkspace(ev) {
        const searchKeywords: SearchKeyword[] = [
            this.getSearchKeyword(
                nameof<BusinessNavigatorOverviewModel>((x) => x.score),
                ev.point.name,
                ComparisonType["Is equal to"]
            ),
        ];

        const preset = await this.getBasePreset(searchKeywords);
        this.openPreset(this.pages.BusinessNavigator, preset);
    }

    protected async onGlobalOpenClick() {
        const preset = await this.getBasePreset();
        this.openPreset(this.pages.BusinessNavigator, preset);
    }
}
